import React, { useContext, useEffect, useState } from 'react';
import { HttpMethods, FetchContext, ApiRoutes } from '../../contexts';
import { useAggregatedState } from '../../../helpers';
import { UserContext } from './../../contexts';

export const AllProjectsContext = React.createContext(null);
const { Provider } = AllProjectsContext;

export const AllProjectsProvider = ({ ...props }) => {
    const { fetchAuthorized } = useContext(FetchContext);

    const {
        closeMenuRequest
    } = useContext(UserContext);

    const [state, setState] = useAggregatedState({
        roles: null,
        adminRoleInfo: null,
        ownerRoleInfo: null,
        pmRoleInfo: null,
        viewerRoleInfo: null,
        lookUpData: null,
        isLoadingProjects: false,
        allProjects: null,
        //allMilestones: null,
        milestone: null,
        userViews: null,
        updatedView: null,
        areProjectsSaved: null,
        errorMilestone: null,
        missingFields: null,
        isLoadingProject: false,
        project: null,
        isRestoringProject: false,
        isSettingProjectIsLocked: false,
        specularProjects: null,
        sapProjects: null,
        bwProjects: null,
        revertedProject: null
    });

    const getRolesAsync = async () => {
        try {
            const r = await fetchAuthorized(ApiRoutes.Role.GetRoles(),
                {
                    method: HttpMethods.GET
                });

            const roles = await r.json();
            setState({ roles });

        } catch (e) {
            console.debug(e)
            //trackError(e);
        }
    }

    const getRoleInfoAsync = async (roleId) => {
        try {
            const response = await fetchAuthorized(ApiRoutes.Role.GetRole({ roleId, withUsers: true }), { method: HttpMethods.GET });
            const roleInfo = await response.json();

            if (roleInfo.groupName == "Administrators")
                setState({ adminRoleInfo: roleInfo });
            else if (roleInfo.groupName == "Owners")
                setState({ ownerRoleInfo: roleInfo });
            else if (roleInfo.groupName == "Project Managers")
                setState({ pmRoleInfo: roleInfo });
            else
                setState({ viewerRoleInfo: roleInfo });
        } catch (e) {
            console.debug(e)
            //trackError(e);
        }
    }

    const getProjectManagersAsync = async ownerKey => {
        const response = await fetchAuthorized(
            ApiRoutes.User.GetMyProjectTeam({ ownerKey }),
            { method: HttpMethods.GET }
        );
        const pmsOwner = await response.json();
        return pmsOwner;
    }

    const getLookupDataAsync = async () => {
        try {
            const r = await fetchAuthorized(ApiRoutes.Data.GetLookupData(),
                {
                    method: HttpMethods.GET
                });

            const lookUpData = await r.json();
            setState({ lookUpData });
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
        }
    }

    const getProjectsAsync = async (data) => {
        try {
            closeMenuRequest(false);
            setState({ isLoadingProjects: true });

            const getProjectsApi = await fetchAuthorized(ApiRoutes.Projects.GetProjects({ year: data, pageSize: 10000 }),
                {
                    method: HttpMethods.GET
                });

            const allProjects = await getProjectsApi.json();
            setState({ allProjects: allProjects.items, isLoadingProjects: false }); //pagination is skipped
            closeMenuRequest(true);
        }

        catch (ex) {
            setState({ isLoadingProjects: false });

            closeMenuRequest(true);
            console.error(ex);
            console.error(ex.details);
        }
    }

    const saveProjectsAsync = async (data) => {
        try {
            setState({ areProjectsSaved: null, missingFields: null });
            
            const saveProjectsApi = await fetchAuthorized(ApiRoutes.Projects.SaveProjects(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                });

            const allProjects = await saveProjectsApi.json();
            setState({ allProjects, areProjectsSaved: true });
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
            setState({ areProjectsSaved: false, missingFields: ex.details });
        }
    }

    const getSapProjectsAsync = async () => {
        try {
            const getSapProjectsApi = await fetchAuthorized(ApiRoutes.Projects.GetSapProjects(),
                {
                    method: HttpMethods.GET
                });

            const sapProjects = await getSapProjectsApi.json();
            setState({ sapProjects: sapProjects.items });
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
        }
    }

    const getBwProjectsAsync = async year => {
        try {
            const getBwProjectsApi = await fetchAuthorized(
                ApiRoutes.Projects.GetBwProjects({ year }),
                { method: HttpMethods.GET }
            );

            const bwProjects = await getBwProjectsApi.json();
            setState({ bwProjects });
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
        }
    }

    const saveMilestoneAsync = async (data) => {
        try {
            setState({ errorMilestone: null });
            const saveMilestonesApi = await fetchAuthorized(ApiRoutes.Projects.SaveMilestone(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                });

            const milestone = await saveMilestonesApi.json();
            setState({ milestone, errorMilestone: false });
        }

        catch (ex) {
            setState({ errorMilestone: true });
            console.error(ex);
            console.error(ex.details);
        }
    }

    const deleteMilestoneAsync = async (data) => {
        try {
            await fetchAuthorized(ApiRoutes.Projects.DeleteMilestone({ milestoneId: data }),
                {
                    method: HttpMethods.DELETE
                });
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
        }
    }

    const getUserSettingsAsync = async (params) => {
        try {
            const gridSettingsApi = await fetchAuthorized(ApiRoutes.User.GetUserSettings(params),
                {
                    method: HttpMethods.GET
                });

            const userViews = await gridSettingsApi.json();
            setState({ userViews, areProjectsSaved: null }); //since getUserSettingsAsync is triggered by changing year and areProjectsSaved makes load current filters instead of the view's ones
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
        }
    }

    const saveUserSettingsAsync = async (data) => {
        try {
            const saveGridSettingsApi = await fetchAuthorized(ApiRoutes.User.SaveUserSettings(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                });

            const updatedView = await saveGridSettingsApi.json();
            setState({ updatedView });
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
        }
    }

    const getProjectAsync = async (data) => {
        try {
            setState({ isLoadingProject: true });
            const getProjectApi = await fetchAuthorized(ApiRoutes.Projects.GetProject({ projectId: data }),
                {
                    method: HttpMethods.GET
                });

            const project = await getProjectApi.json();
            setState({ project, isLoadingProject: false });
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
        }
    }

    const setRestoredProjectAsync = async (log) => {
        try {
            setState({ isRestoringProject: true });
            const getLogApi = await fetchAuthorized(ApiRoutes.Projects.GetProjectSnapshot({ projectChangeId: log.changed.projectDataChangeId }),
                {
                    method: HttpMethods.GET
                });

            const revertedLog = await getLogApi.json();
            setState({ revertedProject: revertedLog, isRestoringProject: false });
        }
        catch (ex) {
            setState({ isRestoringProject: false });
            console.error(ex);
            console.error(ex.details);
        }
    }

    const getSpecularProjectsAsync = async (data) => {
        try {
            const getSpecularProjectsApi = await fetchAuthorized(ApiRoutes.Projects.GetSpecularProjects({ projectId: data }),
                {
                    method: HttpMethods.GET
                });

            const specularProjects = await getSpecularProjectsApi.json();
            setState({ specularProjects: specularProjects.items });
        }

        catch (ex) {
            console.error(ex);
            console.error(ex.details);
        }
    }

    return (
        <Provider value={{
            state,
            getRolesAsync,
            getRoleInfoAsync,
            getProjectManagersAsync,
            getLookupDataAsync,
            getProjectsAsync,
            saveProjectsAsync,
            saveMilestoneAsync,
            deleteMilestoneAsync,
            getUserSettingsAsync,
            saveUserSettingsAsync,
            getProjectAsync,
            setRestoredProjectAsync,
            getSpecularProjectsAsync,
            getSapProjectsAsync,
            getBwProjectsAsync
        }}>
            {props.children}
        </Provider>
    );
}